import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronsUpDown,
  Plus,
  Pencil,
  Trash2,
  Folder,
  Check,
  X,
  Shield,
  BarChart3,
  Briefcase,
  Home,
  Car,
  Wallet,
  TrendingUp,
  Building2,
  Stethoscope,
  LucideIcon,
  AlertTriangle,
} from 'lucide-react';
import { useWorkspace } from '../hooks/useWorkspace';
import { useUser } from '../hooks/useUser';
import { useToast } from '../hooks/useToast';

const WORKSPACE_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

// Lucide icon names for workspaces
const WORKSPACE_ICON_NAMES = ['BarChart3', 'Briefcase', 'Home', 'Car', 'Wallet', 'TrendingUp', 'Building2', 'Stethoscope'];

// Map icon names to components
const iconComponents: Record<string, LucideIcon> = {
  BarChart3,
  Briefcase,
  Home,
  Car,
  Wallet,
  TrendingUp,
  Building2,
  Stethoscope,
  Folder,
};

// Helper to render workspace icon
function WorkspaceIcon({ icon, className = 'w-5 h-5 text-white' }: { icon?: string; className?: string }) {
  // Check if it's a legacy emoji
  if (icon && icon.length <= 2 && /\p{Emoji}/u.test(icon)) {
    return <span className={className}>{icon}</span>;
  }
  const IconComponent = iconComponents[icon || ''] || Folder;
  return <IconComponent className={className} />;
}

export default function WorkspaceSelector() {
  const {
    workspaces,
    currentWorkspace,
    loading,
    setCurrentWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
  } = useWorkspace();

  const { user } = useUser();
  const { showToast, confirm } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showBlockedAlert, setShowBlockedAlert] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<{ id: string; name: string; description?: string; color?: string; icon?: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: WORKSPACE_COLORS[0],
    icon: WORKSPACE_ICON_NAMES[0],
  });

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openCreateModal = () => {
    // Keep the legacy support mailbox restricted from creating new workspaces
    if (user?.email === 'support@pongobot.xyz') {
      setIsOpen(false);
      setShowBlockedAlert(true);
      return;
    }
    setIsOpen(false);
    setIsEditing(false);
    setEditingWorkspace(null);
    setFormData({
      name: '',
      description: '',
      color: WORKSPACE_COLORS[0],
      icon: WORKSPACE_ICON_NAMES[0],
    });
    setIsModalOpen(true);
  };

  const openEditModal = (workspace: { id: string; name: string; description?: string; color?: string; icon?: string }) => {
    setIsOpen(false);
    setIsEditing(true);
    setEditingWorkspace(workspace);
    setFormData({
      name: workspace.name,
      description: workspace.description || '',
      color: workspace.color || WORKSPACE_COLORS[0],
      icon: workspace.icon || WORKSPACE_ICON_NAMES[0],
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing && editingWorkspace) {
        await updateWorkspace(editingWorkspace.id, formData);
      } else {
        const newWorkspace = await createWorkspace(
          formData.name,
          formData.description,
          formData.color,
          formData.icon
        );
        setCurrentWorkspace(newWorkspace);
      }
      setIsModalOpen(false);
    } catch (error: unknown) {
      console.error('Failed to save workspace:', error);
      // Show user-friendly error for workspace limit
      const err = error as { response?: { data?: { code?: string; error?: string } } };
      if (err?.response?.data?.code === 'WORKSPACE_LIMIT_REACHED') {
        showToast('warning', err.response.data.error || 'Limite workspace raggiunto');
      } else if (err?.response?.data?.error) {
        showToast('error', err.response.data.error);
      } else {
        showToast('error', 'Errore durante la creazione del workspace');
      }
    }
  };

  const handleDelete = async (workspace: { id: string; name: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    if (workspaces.length <= 1) {
      showToast('warning', 'Non puoi eliminare l\'ultimo workspace');
      return;
    }
    const confirmed = await confirm({
      title: 'Elimina Workspace',
      message: `Sei sicuro di voler eliminare "${workspace.name}"? Tutti i dati associati saranno persi.`,
      confirmText: 'Elimina',
      type: 'danger'
    });
    if (confirmed) {
      try {
        await deleteWorkspace(workspace.id);
        if (currentWorkspace?.id === workspace.id && workspaces.length > 1) {
          const remaining = workspaces.filter((w) => w.id !== workspace.id);
          if (remaining.length > 0) {
            setCurrentWorkspace(remaining[0]); // This will trigger a page reload
          }
        } else {
          // Deleted a different workspace, just reload to refresh the list
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to delete workspace:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="px-3 py-2">
        <div className="animate-pulse h-10 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <>
      <div className="px-4 py-2" ref={dropdownRef}>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: currentWorkspace?.color || WORKSPACE_COLORS[0] }}
            >
              <WorkspaceIcon icon={currentWorkspace?.icon} className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-[11px] font-medium text-gray-900 dark:text-white truncate">
                {currentWorkspace?.name || 'Workspace'}
              </p>
            </div>
            <ChevronsUpDown className="w-5 h-5 text-gray-400" />
          </button>

          {/* Dropdown - opens upward */}
          {isOpen && (
            <div className="absolute left-0 right-0 bottom-full mb-2 bg-white dark:bg-slate-800 rounded-lg shadow-elevated ring-1 ring-black ring-opacity-5 dark:ring-slate-700 z-50 overflow-hidden">
              <div className="py-1 max-h-64 overflow-y-auto">
                {workspaces.map((workspace) => (
                  <div
                    key={workspace.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 ${
                      currentWorkspace?.id === workspace.id ? 'bg-gray-100 dark:bg-slate-700' : ''
                    }`}
                    onClick={() => {
                      setCurrentWorkspace(workspace);
                      setIsOpen(false);
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: workspace.color || WORKSPACE_COLORS[0] }}
                    >
                      <WorkspaceIcon icon={workspace.icon} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{workspace.name}</p>
                      {workspace.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{workspace.description}</p>
                      )}
                    </div>
                    {currentWorkspace?.id === workspace.id && (
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(workspace);
                      }}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                    >
                      <Pencil className="w-4 h-4 text-gray-400" />
                    </button>
                    {workspaces.length > 1 && (
                      <button
                        onClick={(e) => handleDelete(workspace, e)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 dark:border-slate-700 p-2">
                <button
                  onClick={openCreateModal}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nuovo Workspace
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal - using portal to render outside aside */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] overflow-y-auto flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsModalOpen(false)} />
          <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-modal">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>

            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {isEditing ? 'Modifica Workspace' : 'Nuovo Workspace'}
            </h3>

            {/* Privacy Notice */}
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-green-800 dark:text-green-300">I tuoi dati sono protetti</p>
                <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">
                  Ogni file caricato è isolato e accessibile solo a te. I dati di ciascun workspace sono completamente separati.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Es: Conto Personale"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrizione (opzionale)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Es: Entrate e uscite personali"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Colore</label>
                <div className="flex gap-2 flex-wrap">
                  {WORKSPACE_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-lg transition-transform ${
                        formData.color === color ? 'ring-2 ring-gray-900 dark:ring-white scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Icona</label>
                <div className="flex gap-2 flex-wrap">
                  {WORKSPACE_ICON_NAMES.map((iconName) => {
                    const IconComp = iconComponents[iconName] || Folder;
                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: iconName })}
                        className={`w-10 h-10 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center transition-transform ${
                          formData.icon === iconName ? 'ring-2 ring-gray-900 dark:ring-white scale-110' : ''
                        }`}
                      >
                        <IconComp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {isEditing ? 'Salva' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Blocked alert modal for the legacy support mailbox */}
      {showBlockedAlert && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-modal p-6 w-full max-w-md animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Account Demo</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Funzionalità limitata</p>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Questo è un account demo condiviso. La creazione di nuovi workspace è disabilitata per proteggere i dati degli altri utenti.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Per creare i tuoi workspace personali, registra un nuovo account gratuito con la tua email.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowBlockedAlert(false)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Ho capito
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
